import CheckSmallIcon from 'assets/icons/check-small.svg';
import ExactlyForMeIcon from 'assets/icons/exactly-for-me.svg';
import cn from 'classnames';
import useMobile from 'hooks/common/use-mobile';
import LazyLoadNameInput from 'modules/foodaid/components/lazy-load-name-input';
import Image from "next/legacy/image";
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { gtm } from 'tracking';
import { Gtm } from 'tracking/gtm';

interface Props {
  className?: string;
  image: string;
  petType: 'dogs' | 'cats';
}

/**
 * FubeEntryPoint component displays a landing section with customized content based on pet type
 */
const FubeEntryPoint: React.FC<Props> = ({ className, image, petType }) => {
  const { formatMessage } = useIntl();
  const isMobile = useMobile();

  const clickTracking = (trackingStr: string): void => {
    gtm({
      group: Gtm.GroupName.Component,
      name: Gtm.ComponentEventName.Click,
      data: { label: trackingStr },
    });
  };

  const ListItem = ({
    messageId,
    petType,
  }: {
    messageId: string;
    petType?: 'dogs' | 'cats';
  }): JSX.Element => (
    <li className="mb-3 flex items-start" data-test="fube-list-item">
      <CheckSmallIcon className="mr-2 h-6 w-6 shrink-0" />
      <span>
        <FormattedMessage
          id={petType ? `${messageId}:${petType}` : messageId}
          values={{
            span: (chunks) => <span className="font-medium">{chunks}</span>,
          }}
        />
      </span>
    </li>
  );

  const ImageWithIcon = ({
    className,
    petType,
    src,
  }: {
    className?: string;
    petType: 'dogs' | 'cats';
    src: string;
  }): JSX.Element => (
    <div
      className={cn('relative w-full overflow-hidden', className)}
      data-test="image-container"
    >
      <Image
        src={src}
        placeholder="blur"
        alt={formatMessage({ id: 'landingpage:home:fube-entry:image-alt ' })}
        priority
        layout="fill"
        objectFit="cover"
        className={cn(
          petType === 'dogs'
            ? 'translate-x-10 scale-150 lg:translate-x-0 lg:scale-100'
            : 'lg:scale-100'
        )}
        quality={100}
        data-test="FuBe-Entry-Image"
      />
      <ExactlyForMeIcon
        className={cn(
          'absolute left-0 top-0 h-auto w-44 lg:bottom-0 lg:left-auto lg:right-0 lg:top-auto lg:mb-16 lg:mr-8 lg:w-64',
          petType === 'dogs' ? 'text-dog-highlight' : 'text-cat-highlight'
        )}
      />
    </div>
  );

  return (
    <div
      className={cn(
        'relative mt-8 text-text-dark-bg-primary lg:mt-0',
        petType === 'dogs' ? 'bg-dog-base' : 'bg-cat-base',
        className
      )}
      data-test="fube-entry-container"
    >
      <div
        className={cn(
          'col-span-1 grid lg:grid-cols-12',
          !isMobile && 'container'
        )}
      >
        <div className="col-span-6">
          <ImageWithIcon
            className="h-[300px] lg:absolute lg:right-0 lg:top-0 lg:h-full lg:w-1/2"
            petType={petType}
            src={image}
          />
          <div className={cn('mt-5 pb-6 lg:py-24', isMobile && 'container')}>
            <p className="mindset-28" data-test="fube-headline">
              <FormattedMessage id="landingpage:home:fube-entry:headline" />
            </p>
            <div className="mt-4 lg:mt-5 lg:max-w-[472px]">
              <LazyLoadNameInput
                petType={petType}
                submitCallback={() =>
                  clickTracking('controlled-fube-start-on-top')
                }
              />
              <div className="text-sm lg:text-base">
                <ul className="leading-tight text-text-dark-bg-secondary lg:leading-6">
                  <ListItem messageId="landingpage:home:fube-entry:usp-1" />
                  <ListItem
                    messageId="landingpage:home:fube-entry:usp-2"
                    petType={petType}
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(FubeEntryPoint);
